(() => {
  if (customElements.get('juli-cart-recommendations')) return;
  const cache = new Map();
  let contextToken = null;
  let added = new Set();
  let recentSource = null;
  let pendingRecentSource = null;
  let previousHeight = 0;
  const storageKey = token => `juli-upsell-added:${token}`;
  const recentKey = token => `juli-upsell-recent:${token}`;
  function save() {
    try { sessionStorage.setItem(storageKey(contextToken), JSON.stringify([...added])); } catch (_) {}
  }
  function saveRecent() {
    try {
      if (recentSource) localStorage.setItem(recentKey(contextToken), String(recentSource));
      else localStorage.removeItem(recentKey(contextToken));
    } catch (_) {}
  }
  function setContext(state) {
    const token = state.token || window.liquidAjaxCart?.cart?.token || 'session';
    if (contextToken !== token) {
      contextToken = token;
      previousHeight = 0;
      try { added = new Set(JSON.parse(sessionStorage.getItem(storageKey(contextToken)) || '[]')); }
      catch (_) { added = new Set(); }
      try { recentSource = Number(localStorage.getItem(recentKey(contextToken))) || null; }
      catch (_) { recentSource = null; }
    }
    const present = new Set(state.products);
    added = new Set([...added].filter(id => present.has(id)));
    // The add response can arrive before the replacement cart section.
    if (pendingRecentSource && present.has(pendingRecentSource)) {
      recentSource = pendingRecentSource;
      pendingRecentSource = null;
    }
    if (recentSource && !present.has(recentSource)) recentSource = null;
    if (!present.size) previousHeight = 0;
    save();
    saveRecent();
  }
  function choose(state) {
    const present = new Set(state.products);
    const recent = present.has(recentSource) && !added.has(recentSource) ? recentSource : null;
    const candidates = (window.juliCartPlan || []).filter(row => present.has(row.source) &&
      !added.has(row.source) && !present.has(row.candidate));
    const group = row => row.priority > 2 ? (row.source === recent ? 2 : 3) :
      row.source === recent ? 0 : 1;
    candidates.sort((a, b) => group(a) - group(b) || a.priority - b.priority ||
      Number(b.supported) - Number(a.supported) ||
      (a.supported ? b.joint - a.joint || b.share - a.share || b.lift - a.lift :
        b.popularity - a.popularity) || a.candidate - b.candidate);
    const unique = new Set();
    return candidates.filter(row => !unique.has(row.candidate) && unique.add(row.candidate));
  }
  async function card(handle) {
    const root = window.Shopify?.routes?.root || '/';
    const key = root + handle;
    const old = cache.get(key);
    if (old && Date.now() - old.time < 60000) return old.promise;
    const promise = fetch(`${root}products/${encodeURIComponent(handle)}?section_id=juli-recommendation-card`,
      { credentials: 'same-origin' }).then(response => {
      if (!response.ok) throw new Error('Recommendation unavailable');
      return response.text();
    });
    cache.set(key, { time: Date.now(), promise });
    try { return await promise; } catch (error) { cache.delete(key); throw error; }
  }
  class CartRecommendations extends HTMLElement {
    connectedCallback() {
      const data = this.querySelector('[data-cart-context]');
      if (!data) return;
      this.state = JSON.parse(data.textContent);
      setContext(this.state);
      if (previousHeight && this.state.products.length) {
        this.hidden = false;
        this.style.minHeight = `${previousHeight}px`;
      }
      this.refresh();
    }
    async refresh() {
      const generation = this.generation = (this.generation || 0) + 1;
      const selected = choose(this.state);
      const elements = [];
      // Only approved complements are eligible. Fetch the next ranked batch
      // if a product has become unavailable; never invent a new fallback.
      let cursor = 0;
      while (elements.length < 2 && cursor < selected.length) {
        const batch = selected.slice(cursor, cursor + 2 - elements.length);
        cursor += batch.length;
        const fragments = await Promise.all(batch.map(async row => {
          try { return await card(row.handle); } catch (_) { return null; }
        }));
        if (!this.isConnected || this.generation !== generation) return;
        for (const markup of fragments) {
          if (!markup) continue;
          const doc = new DOMParser().parseFromString(markup, 'text/html');
          const element = doc.querySelector('juli-upsell-card');
          if (element) elements.push(element);
        }
      }
      if (!this.isConnected || this.generation !== generation) return;
      // An add finishes while its replacement section is connecting. Recheck
      // its source eligibility after the request's firstCallback records it.
      const current = choose(this.state);
      if (selected.some((row, index) => row.candidate !== current[index]?.candidate)) return this.refresh();
      const scroll = this.closest('.juli-slidecart__scroll');
      const position = scroll?.scrollTop;
      const cards = document.createElement('div');
      cards.className = 'juli-upsell__cards';
      cards.append(...elements);
      this.replaceChildren();
      this.style.minHeight = '';
      this.hidden = !cards.children.length;
      if (!this.hidden) {
        this.setAttribute('role', 'region');
        this.setAttribute('aria-label', this.dataset.heading);
        const heading = document.createElement('h2');
        heading.className = 'juli-upsell__heading';
        heading.textContent = this.dataset.heading;
        this.append(heading, cards);
      }
      previousHeight = this.getBoundingClientRect().height;
      if (scroll && position !== undefined) scroll.scrollTop = position;
      window.JuliUpsellFocus?.restore();
    }
  }
  window.JuliCartRecommendations = {
    markAdded(id) {
      if (!Number.isFinite(id)) return;
      const current = document.querySelector('juli-cart-recommendations');
      if (current?.state) setContext(current.state);
      if (window.liquidAjaxCart?.cart?.items?.some(item => item.product_id === id)) return;
      added.add(id);
      save();
    }
  };
  // A regular product-form add is a deliberate main-product choice.
  document.addEventListener('liquid-ajax-cart:request-end', event => {
    const state = event.detail?.requestState;
    if (state?.requestType !== 'add' || !state.responseData?.ok || state.info?.juliInlineUpsell) return;
    const body = state.responseData.body;
    const items = body?.items || (body?.product_id ? [body] : []);
    for (const item of items) added.delete(item.product_id);
    const source = Number(items.at(-1)?.product_id);
    if (Number.isFinite(source) && source > 0) {
      recentSource = source;
      pendingRecentSource = source;
      saveRecent();
    }
    save();
    document.querySelector('juli-cart-recommendations')?.refresh();
  });
  customElements.define('juli-cart-recommendations', CartRecommendations);
})();
